import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { getLocationBySlug, locationFaqs } from "@/data/locations";
import { getCourseBySlug, getCoursesByCategory, courseCategories } from "@/data/courses";
import { MapPin, BookOpen, Phone, MessageCircle, ChevronRight, ArrowRight, Trophy, Star, Users, Clock, CheckCircle, HelpCircle, ImageIcon, GraduationCap, Target, FileText } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

export default function LocationCoursePage() {
  const { locationSlug, courseSlug } = useParams();
  const location = getLocationBySlug(locationSlug);
  const course = getCourseBySlug(courseSlug);

  if (!location || !course) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center">
        <h1 className="text-3xl font-bold text-vedanti-navy mb-4">Page Not Found</h1>
        <Link to="/" className="text-vedanti-orange hover:underline">Go to Homepage</Link>
      </div>
    );
  }

  const category = courseCategories.find((c) => c.id === course.category);
  const relatedCourses = getCoursesByCategory(course.category).filter((c) => c.slug !== course.slug).slice(0, 6);
  const pageTitle = `${course.name} Coaching in ${location.name} | Vedanti Academy`;
  const pageDesc = `Best ${course.name} coaching near ${location.name}. ${course.fullName} preparation at Vedanti Academy with expert faculty, AC classrooms, Vedic Math shortcuts, and 'Upto Selection Support'. ${location.distance} from our branch. Call +91 8308776505.`;

  const examPattern = course.examPattern;
  const patternEntries = examPattern ? Object.entries(examPattern) : [];

  const courseFaqs = [
    {
      q: `Where can I get ${course.name} coaching near ${location.name}?`,
      a: `Vedanti Academy is the best ${course.name} coaching institute near ${location.name}. Our branch in Badlapur is easily accessible. ${location.connectivity || `We are ${location.distance} from ${location.name}.`} We also offer hybrid online+offline classes for maximum flexibility.`,
    },
    {
      q: `What is the fee for ${course.name} coaching at Vedanti Academy?`,
      a: `Our ${course.name} coaching fees are very competitive and affordable. We offer multiple batch options and payment plans. Contact us at +91 8308776505 for the latest fee structure. We also offer free demo classes before enrollment.`,
    },
    {
      q: `What is the eligibility for ${course.name} exam?`,
      a: course.eligibility || `Please contact us at +91 8308776505 for detailed eligibility criteria for the ${course.name} exam.`,
    },
    {
      q: `How long does it take to prepare for ${course.name}?`,
      a: `With dedicated preparation at Vedanti Academy, most students are exam-ready within 4-6 months for ${course.name}. We offer both regular and weekend batches. Our 'Upto Selection Support' means you get guidance until you clear the exam — no fixed end dates.`,
    },
    {
      q: `Does Vedanti Academy provide ${course.name} study material for ${location.name} students?`,
      a: `Yes! We provide comprehensive ${course.name} study material including textbooks, topic-wise notes, previous year papers, daily practice sets, and access to our online mock test series. All materials are updated for the latest exam pattern.`,
    },
    {
      q: `Is online coaching available for ${course.name} from ${location.name}?`,
      a: `Absolutely! Our 360-degree hybrid model allows ${location.name} students to attend live online ${course.name} classes, access recorded lectures, and get doubts cleared via WhatsApp/Zoom anytime during the week.`,
    },
    {
      q: `What batch timings are available for ${course.name} in ${location.name}?`,
      a: `We offer Morning (8-10 AM), Afternoon (12-2 PM), Evening (5-7 PM), and Weekend batches for ${course.name}. ${location.name} students can choose the most convenient timing for their commute.`,
    },
    {
      q: `What is the success rate for ${course.name} at Vedanti Academy?`,
      a: `Vedanti Academy has produced 31+ achievers across Banking, SSC, MBA, and Railway exams. Our ${course.name} students have consistently performed well with our proven methodology of Vedic Math shortcuts, personalized mentorship, and unlimited mock tests.`,
    },
  ];

  return (
    <div data-testid={`location-course-page-${location.slug}-${course.slug}`}>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDesc} />
        <meta name="keywords" content={`${course.name} coaching ${location.name}, ${course.name} classes ${location.name}, ${course.fullName} preparation ${location.name}, Vedanti Academy ${course.name}, best ${course.name} coaching near ${location.name}`} />
        <meta property="og:title" content={`${course.name} Coaching in ${location.name} | Vedanti Academy`} />
        <meta property="og:description" content={pageDesc} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`https://vedantiacademy.com/locations/${location.slug}/${course.slug}`} />
        <link rel="canonical" href={`https://vedantiacademy.com/locations/${location.slug}/${course.slug}`} />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Course",
          "name": `${course.name} Coaching in ${location.name} - Vedanti Academy`,
          "description": pageDesc,
          "url": `https://vedantiacademy.com/locations/${location.slug}/${course.slug}`,
          "provider": {
            "@type": "EducationalOrganization",
            "name": "Vedanti Academy",
            "url": "https://vedantiacademy.com",
            "telephone": "+918308776505",
            "address": { "@type": "PostalAddress", "streetAddress": "3rd Floor, Jadhav Market, above Aradhana Hotel", "addressLocality": "Badlapur", "addressRegion": "Maharashtra", "postalCode": "421503", "addressCountry": "IN" }
          },
          "educationalLevel": "Competitive Exam Preparation",
          "courseMode": "Blended",
          "hasCourseInstance": {
            "@type": "CourseInstance",
            "courseMode": ["Onsite", "Online"],
            "courseWorkload": "PT10H",
            "location": { "@type": "Place", "name": `Vedanti Academy ${location.name}`, "address": { "@type": "PostalAddress", "streetAddress": "3rd Floor, Jadhav Market, above Aradhana Hotel", "addressLocality": "Badlapur", "addressRegion": "Maharashtra", "postalCode": "421503", "addressCountry": "IN" } }
          },
          "offers": { "@type": "Offer", "category": "EducationalProgram", "availability": "https://schema.org/InStock", "priceCurrency": "INR", "price": "0", "url": `https://vedantiacademy.com/locations/${location.slug}/${course.slug}` },
          "areaServed": { "@type": "Place", "name": location.name }
        })}</script>
      </Helmet>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-vedanti-navy"></div>
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(249,115,22,0.2)_0%,_transparent_50%)]"></div>
        <div className="absolute top-10 right-20 w-64 h-64 bg-vedanti-orange/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-0 left-0 right-0 h-1.5 bg-gradient-to-r from-vedanti-orange via-vedanti-mango to-vedanti-orange"></div>
        <div className="container-main py-14 md:py-20 relative z-10">
          {/* Breadcrumbs */}
          <div className="flex items-center gap-2 text-sm text-slate-400 mb-5 flex-wrap">
            <Link to="/" className="hover:text-white transition">Home</Link>
            <ChevronRight size={14} />
            <Link to={`/locations/${location.slug}`} className="hover:text-white transition">{location.name}</Link>
            <ChevronRight size={14} />
            <span className="text-vedanti-mango">{course.name}</span>
          </div>
          <div className="flex items-center gap-2 mb-3">
            <MapPin size={16} className="text-vedanti-mango" />
            <span className="text-vedanti-mango text-sm font-semibold">{location.name} &middot; {location.distance} from branch</span>
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white leading-tight">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-vedanti-orange to-vedanti-mango">{course.name}</span> Coaching in {location.name}
          </h1>
          <p className="text-slate-300 mt-4 text-lg max-w-3xl">{course.description}</p>
          <div className="flex flex-wrap gap-4 mt-8">
            <Link to="/demo-class" className="bg-gradient-to-r from-vedanti-orange to-amber-500 text-white px-7 py-3.5 rounded-xl font-bold shadow-lg hover:-translate-y-0.5 transition-all inline-flex items-center gap-2" data-testid="hero-demo-btn">
              Book Free Demo <ArrowRight size={18} />
            </Link>
            <a href="tel:+918308776505" className="border-2 border-white/30 text-white hover:bg-white/10 px-7 py-3.5 rounded-xl font-semibold transition-all inline-flex items-center gap-2">
              <Phone size={18} /> Call Now
            </a>
            <Link to="/consultancy" className="border-2 border-white/30 text-white hover:bg-white/10 px-7 py-3.5 rounded-xl font-semibold transition-all">
              Free Consultancy
            </Link>
          </div>
        </div>
      </section>

      {/* Course Info Cards */}
      <section className="bg-white border-b">
        <div className="container-main py-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-blue-50 p-4 rounded-xl">
              <GraduationCap size={20} className="text-blue-600 mb-2" />
              <div className="font-bold text-vedanti-navy text-sm">Full Name</div>
              <div className="text-slate-600 text-xs mt-1">{course.fullName}</div>
            </div>
            <div className="bg-green-50 p-4 rounded-xl">
              <Target size={20} className="text-green-600 mb-2" />
              <div className="font-bold text-vedanti-navy text-sm">Eligibility</div>
              <div className="text-slate-600 text-xs mt-1">{course.eligibility || "Contact for details"}</div>
            </div>
            <div className="bg-amber-50 p-4 rounded-xl">
              <Clock size={20} className="text-amber-600 mb-2" />
              <div className="font-bold text-vedanti-navy text-sm">Batch Info</div>
              <div className="text-slate-600 text-xs mt-1">{course.batchInfo || "Regular & Weekend batches"}</div>
            </div>
            <div className="bg-purple-50 p-4 rounded-xl">
              <MapPin size={20} className="text-purple-600 mb-2" />
              <div className="font-bold text-vedanti-navy text-sm">From {location.name}</div>
              <div className="text-slate-600 text-xs mt-1">{location.connectivity || location.distance}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Vedanti for this course */}
      <section className="section-padding bg-gradient-to-b from-white to-slate-50">
        <div className="container-main">
          <h2 className="font-heading text-2xl md:text-3xl font-extrabold text-vedanti-navy mb-6">
            Why Choose Vedanti Academy for <span className="text-vedanti-orange">{course.name}</span> in {location.name}?
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { title: "Expert Faculty", desc: `Our ${course.name} faculty brings years of experience in competitive exam coaching with proven strategies and shortcuts.` },
              { title: "Updated Syllabus & Pattern", desc: `Complete ${course.name} preparation based on the latest exam pattern and syllabus with regularly updated study material.` },
              { title: "Mock Test Series", desc: `Unlimited ${course.name} mock tests simulating real exam conditions with detailed analysis and performance tracking.` },
              { title: "Vedic Math & Shortcuts", desc: `Learn unique calculation shortcuts and Vedic Math techniques to solve ${course.name} Quantitative/Reasoning questions faster.` },
              { title: "Upto Selection Support", desc: `We support you until you crack ${course.name} — no fixed course end dates. Attend classes until you get selected.` },
              { title: `Flexible for ${location.name}`, desc: `Morning, afternoon, evening & weekend batches plus online hybrid classes designed for ${location.name} students' convenience.` },
            ].map((item) => (
              <div key={item.title} className="bg-white p-5 rounded-xl border border-slate-100 shadow-sm">
                <CheckCircle size={18} className="text-vedanti-orange mb-2" />
                <h3 className="font-bold text-vedanti-navy text-sm mb-1">{item.title}</h3>
                <p className="text-slate-500 text-xs leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Exam Pattern */}
      {patternEntries.length > 0 && (
        <section className="section-padding bg-slate-50" data-testid="exam-pattern">
          <div className="container-main">
            <h2 className="font-heading text-2xl md:text-3xl font-extrabold text-vedanti-navy mb-8">
              <span className="text-vedanti-orange">{course.name}</span> Exam Pattern
            </h2>
            <div className="space-y-6">
              {patternEntries.map(([stage, data]) => {
                const stageLabel = stage.replace(/([A-Z0-9])/g, " $1").replace(/^./, (s) => s.toUpperCase()).replace(/\b(cbt|tier|paper)\b/gi, (m) => m.toUpperCase().replace("CBT", "CBT ").replace("TIER", "Tier").replace("PAPER", "Paper"));
                const hasSections = Array.isArray(data.sections);
                return (
                <div key={stage} className="bg-white rounded-xl border border-slate-100 overflow-hidden shadow-sm">
                  <div className="bg-vedanti-navy px-5 py-3 flex items-center justify-between flex-wrap gap-2">
                    <h3 className="font-bold text-white capitalize text-sm">{stageLabel}</h3>
                    <div className="flex gap-4 text-xs text-slate-300">
                      {data.totalMarks && <span>Total: {data.totalMarks} marks</span>}
                      {(data.totalTime || data.time) && <span>Time: {hasSections && data.sections.some(s => s.time === "Cumulative") ? `Cumulative (${data.totalTime})` : (data.totalTime || data.time)}</span>}
                    </div>
                  </div>
                  {hasSections ? (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-slate-50 text-left">
                            <th className="px-5 py-2.5 font-semibold text-slate-600 text-xs">Section</th>
                            <th className="px-5 py-2.5 font-semibold text-slate-600 text-xs text-center">Questions</th>
                            <th className="px-5 py-2.5 font-semibold text-slate-600 text-xs text-center">Marks</th>
                            <th className="px-5 py-2.5 font-semibold text-slate-600 text-xs text-center">Time</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.sections.map((s) => (
                            <tr key={s.name} className="border-t border-slate-50">
                              <td className="px-5 py-2.5 text-slate-700 text-xs">{s.name}</td>
                              <td className="px-5 py-2.5 text-center text-slate-600 text-xs">{s.questions}</td>
                              <td className="px-5 py-2.5 text-center font-semibold text-vedanti-navy text-xs">{s.marks}</td>
                              <td className="px-5 py-2.5 text-center text-slate-600 text-xs">{s.time === "Cumulative" ? <span className="italic text-slate-400">Cumulative</span> : s.time}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="p-5 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                      {data.subject && <div><p className="text-slate-500 font-medium">Subject</p><p className="text-vedanti-navy font-bold mt-0.5">{data.subject}</p></div>}
                      {data.questions !== undefined && <div><p className="text-slate-500 font-medium">Questions</p><p className="text-vedanti-navy font-bold mt-0.5">{data.questions}</p></div>}
                      {data.marks && <div><p className="text-slate-500 font-medium">Marks</p><p className="text-vedanti-navy font-bold mt-0.5">{data.marks}</p></div>}
                      {data.time && <div><p className="text-slate-500 font-medium">Duration</p><p className="text-vedanti-navy font-bold mt-0.5">{data.time}</p></div>}
                      {data.nature && <div className="col-span-2 sm:col-span-4 mt-1"><p className="text-amber-700 font-semibold text-[11px] italic">{data.nature}</p></div>}
                    </div>
                  )}
                </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Syllabus */}
      {course.syllabus && (
        <section className="section-padding bg-white" data-testid="syllabus">
          <div className="container-main">
            <h2 className="font-heading text-2xl md:text-3xl font-extrabold text-vedanti-navy mb-6">
              {course.name} Syllabus Covered at Vedanti Academy
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {Object.entries(course.syllabus).map(([section, topics], i) => {
                const accents = ["border-vedanti-orange", "border-vedanti-navy", "border-green-500", "border-vedanti-mango", "border-blue-500", "border-purple-500"];
                const bgAccents = ["bg-orange-50", "bg-blue-50", "bg-green-50", "bg-amber-50", "bg-sky-50", "bg-purple-50"];
                return (
                  <div key={section} className={`rounded-xl border ${accents[i % accents.length]} border-l-4 ${bgAccents[i % bgAccents.length]} p-4`} data-testid={`syllabus-group-${i}`}>
                    <h3 className="font-bold text-vedanti-navy text-sm mb-2.5 flex items-center gap-2">
                      <FileText size={14} className="text-vedanti-orange flex-shrink-0" />
                      {section}
                    </h3>
                    <ul className="space-y-1.5">
                      {topics.map((topic) => (
                        <li key={topic} className="flex items-start gap-2 text-xs text-slate-600">
                          <CheckCircle size={12} className="text-vedanti-orange flex-shrink-0 mt-0.5" />
                          <span>{topic}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Gallery Link */}
      <section className="relative overflow-hidden" data-testid="gallery-cta">
        <div className="absolute inset-0 bg-gradient-to-r from-vedanti-navy via-[#001a3a] to-vedanti-navy"></div>
        <div className="container-main py-12 relative z-10">
          <div className="flex flex-col md:flex-row items-center justify-between gap-5">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <ImageIcon size={18} className="text-vedanti-mango" />
                <span className="text-vedanti-mango font-bold text-xs uppercase tracking-wide">Wall of Fame</span>
              </div>
              <h3 className="font-heading text-xl md:text-2xl font-bold text-white">See students who cracked {category?.name || "competitive exams"} from the region</h3>
              <p className="text-slate-400 mt-1 text-sm">31+ achievers in Banking, MBA, Railway & Government exams</p>
            </div>
            <Link to="/gallery" className="bg-gradient-to-r from-vedanti-orange to-amber-500 text-white px-6 py-3 rounded-xl font-bold shadow-lg hover:-translate-y-0.5 transition-all inline-flex items-center gap-2 whitespace-nowrap text-sm" data-testid="gallery-link">
              <Trophy size={16} /> View Gallery <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* Related Courses */}
      {relatedCourses.length > 0 && (
        <section className="section-padding bg-slate-50" data-testid="related-courses">
          <div className="container-main">
            <h2 className="font-heading text-2xl md:text-3xl font-extrabold text-vedanti-navy mb-6">
              Other {category?.name || "Courses"} in <span className="text-vedanti-orange">{location.name}</span>
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {relatedCourses.map((rc) => (
                <Link key={rc.slug} to={`/locations/${location.slug}/${rc.slug}`} className="bg-white p-4 rounded-xl border border-slate-100 hover:border-vedanti-orange/30 shadow-sm hover:shadow-md transition-all group">
                  <h3 className="font-bold text-vedanti-navy group-hover:text-vedanti-orange transition-colors">{rc.name} in {location.name}</h3>
                  <p className="text-slate-500 text-xs mt-1 line-clamp-2">{rc.fullName}</p>
                  <div className="mt-2 text-vedanti-orange text-xs font-semibold inline-flex items-center gap-1">
                    View Details <ArrowRight size={12} />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQs */}
      <section className="section-padding bg-white" data-testid="faq-section">
        <div className="container-main max-w-4xl">
          <div className="flex items-center gap-3 mb-8">
            <HelpCircle size={24} className="text-vedanti-orange" />
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-vedanti-navy">
              {course.name} Coaching in {location.name} — FAQs
            </h2>
          </div>
          <Accordion type="single" collapsible className="space-y-3">
            {courseFaqs.map((faq, i) => (
              <AccordionItem key={faq.q} value={`faq-${i}`} className="bg-slate-50 rounded-xl border border-slate-100 px-5 shadow-sm">
                <AccordionTrigger className="text-left font-semibold text-vedanti-navy hover:text-vedanti-orange text-sm py-4">
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 text-sm leading-relaxed pb-4">
                  {faq.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-vedanti-orange via-amber-500 to-vedanti-mango"></div>
        <div className="container-main py-14 relative z-10 text-center">
          <h2 className="font-heading text-2xl md:text-4xl font-extrabold text-white mb-3">Start {course.name} Preparation from {location.name} Today!</h2>
          <p className="text-white/90 mb-8 max-w-xl mx-auto">Expert faculty, proven methodology, and &apos;Upto Selection Support&apos;. Book your free demo class now.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/demo-class" className="bg-white text-vedanti-navy hover:bg-vedanti-navy hover:text-white rounded-xl px-7 py-3.5 font-bold shadow-xl hover:-translate-y-0.5 transition-all" data-testid="bottom-demo-btn">
              Book Free Demo Class
            </Link>
            <a href={`https://wa.me/918308776505?text=${encodeURIComponent(`Hi Vedanti Academy 👋\n\nI'm from *${location.name}* and interested in *${course.fullName || course.name}* coaching.\n\nCould you please share:\n• Latest fee chart + EMI options\n• Free demo class slot\n• Upcoming batch start dates\n\nThanks!`)}`} target="_blank" rel="noopener noreferrer nofollow" className="border-2 border-white text-white hover:bg-white hover:text-green-600 rounded-xl px-7 py-3.5 font-semibold transition-all inline-flex items-center gap-2" data-testid="bottom-whatsapp-btn">
              <MessageCircle size={18} /> WhatsApp Us
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
